import React, { useCallback } from 'react'
import { Button, Card, Col, Collapse, Input, Row, Space, Tag, Typography } from 'antd'
import { DeleteOutlined } from '@ant-design/icons'
import { DatahubColumnsEditor } from './DatahubColumnsEditor'
import { DatahubGeneralPanel } from './DatahubGeneralPanel'
import { CnDatahubCategoryAccess, PrivateCategoryConfig } from './datahub-interfaces'

interface CnDatahubPrivateCategoryProps {
  config: PrivateCategoryConfig
  onChange: (config: PrivateCategoryConfig) => void
  onDelete: () => void
}

export function CnDatahubPrivateCategory({
  config,
  onChange,
  onDelete,
}: CnDatahubPrivateCategoryProps) {
  const updateCategory = useCallback(
    (patch: Partial<PrivateCategoryConfig['category']>) => {
      onChange({
        ...config,
        category: { ...config.category, ...patch },
      })
    },
    [config, onChange],
  )

  const updateAccess = useCallback(
    (partialAccess: Partial<CnDatahubCategoryAccess>) => {
      onChange({
        ...config,
        access: { ...config.access, ...partialAccess },
      })
    },
    [config, onChange],
  )

  return (
    <Card
      size="small"
      title={
        <Space>
          <span>{config.category.name || 'Nouvelle catégorie'}</span>
        </Space>
      }
      extra={
        <Button type="text" danger icon={<DeleteOutlined />} onClick={onDelete}>
          Supprimer
        </Button>
      }
      style={{ marginBottom: 8 }}
      styles={{ body: { padding: 12 } }}
    >
      <Collapse
        size="small"
        defaultActiveKey={['info', 'general', 'columns']}
        items={[
          {
            key: 'general',
            label: 'Général',
            children: (
              <DatahubGeneralPanel
                access={config.access}
                onChange={updateAccess}
                categoryNameInput={
                  <>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Nom affiché
                    </Typography.Text>
                    <Input
                      size="small"
                      value={config.category.name}
                      placeholder="Ex: Chantiers"
                      onChange={(e) => updateCategory({ name: e.target.value })}
                      style={{ marginTop: 2 }}
                    />
                  </>
                }
                mode="private"
              />
            ),
          },
          {
            key: 'columns',
            label: <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography.Text strong>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                  Colonnes
                </div>
              </Typography.Text>
              <Tag color="blue">{config.columns.length} colonne{config.columns.length > 1 ? 's' : ''}</Tag>
            </div>,
            children: (
              <DatahubColumnsEditor
                columns={config.columns}
                categoryId={config.category.id}
                mode="private"
                onChange={(columns) => onChange({ ...config, columns })}
              />
            ),
          },
        ]}
      />
    </Card>
  )
}
